/**
 * Right-click menu for a page (page tree, favourites): favourite, recent,
 * copy link, duplicate, rename, move, trash, open in a new tab.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextMenu } from 'radix-ui';
import {
  ArrowUpRight,
  ChevronRight,
  Copy,
  CornerUpRight,
  EyeOff,
  FolderOutput,
  Link2,
  PenLine,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { useStore } from '../db/context';
import { toggleFavorite, useFavorites } from '../lib/favorites';
import { PAGE_KEYS, usePageActions } from '../lib/pageActions';
import { useUi } from '../state/ui';
import { formatKeys } from '../shortcuts';
import { useWorkspaces } from '../workspaces/state';

const content =
  'nx-pop-in z-[70] min-w-60 rounded-xl border border-border bg-bg p-1 text-sm shadow-pop';
const item =
  'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 outline-none data-[disabled]:cursor-default data-[disabled]:opacity-40 data-[highlighted]:bg-bg-hover';

function Key({ keys }: { keys: string }) {
  return <span className="ml-auto pl-4 text-xs text-fg-muted">{formatKeys(keys)}</span>;
}

export function PageMenu({
  pageId,
  title,
  workspaceId,
  onRename,
  onAddChild,
  readOnly,
  children,
}: {
  pageId: string;
  title: string;
  /** The page's own workspace, if any -- offers "share with workspace" only for a private page. */
  workspaceId?: string | null;
  /** Rename in place; without it the page opens with its title selected. */
  onRename?: () => void;
  /** Add a subpage (page tree; on touch screens the only way, there is no hover). */
  onAddChild?: () => void;
  /** A read-only workspace membership: hide every action that writes. */
  readOnly?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const favorites = useFavorites();
  const { recent, forgetRecent, open } = useUi();
  const actions = usePageActions();
  const isFavorite = favorites.includes(pageId);
  const writeWorkspaces = useWorkspaces().filter((w) => w.permission === 'write');

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className={content} aria-label={t('pageMenu.label', { title })}>
          <ContextMenu.Label className="px-2 py-1 text-xs text-fg-muted">
            {t('pageMenu.page')}
          </ContextMenu.Label>
          {onAddChild && !readOnly && (
            <ContextMenu.Item className={item} onSelect={onAddChild}>
              <Plus className="size-4" />
              {t('sidebar.newSubpage')}
            </ContextMenu.Item>
          )}
          <ContextMenu.Item className={item} onSelect={() => void toggleFavorite(store, pageId)}>
            <Star className="size-4" fill={isFavorite ? 'currentColor' : 'none'} />
            {isFavorite ? t('pageMenu.unfavorite') : t('pageMenu.favorite')}
          </ContextMenu.Item>
          <ContextMenu.Item
            className={item}
            disabled={!recent.includes(pageId)}
            onSelect={() => forgetRecent(pageId)}
          >
            <EyeOff className="size-4" />
            {t('pageMenu.forgetRecent')}
          </ContextMenu.Item>
          <ContextMenu.Separator className="my-1 h-px bg-border" />
          <ContextMenu.Item
            className={item}
            onSelect={() => void navigator.clipboard.writeText(`[[${title}]]`)}
          >
            <Link2 className="size-4" />
            {t('pageMenu.copyLink')}
          </ContextMenu.Item>
          {!readOnly && (
            <>
              <ContextMenu.Item className={item} onSelect={() => void actions.duplicate(pageId)}>
                <Copy className="size-4" />
                {t('pageMenu.duplicate')}
                <Key keys={PAGE_KEYS.duplicate} />
              </ContextMenu.Item>
              <ContextMenu.Item
                className={item}
                onSelect={() => (onRename ? onRename() : open({ kind: 'page', pageId }))}
              >
                <PenLine className="size-4" />
                {t('pageMenu.rename')}
                <Key keys={PAGE_KEYS.rename} />
              </ContextMenu.Item>
              <ContextMenu.Item className={item} onSelect={() => actions.move(pageId)}>
                <CornerUpRight className="size-4" />
                {t('pageMenu.move')}
                <Key keys={PAGE_KEYS.move} />
              </ContextMenu.Item>
              <ContextMenu.Item
                className={`${item} data-[highlighted]:text-danger`}
                onSelect={() => actions.trash(pageId)}
              >
                <Trash2 className="size-4" />
                {t('page.moveToTrash')}
              </ContextMenu.Item>
              {!workspaceId && writeWorkspaces.length > 0 && (
                <ContextMenu.Sub>
                  <ContextMenu.SubTrigger className={item}>
                    <FolderOutput className="size-4" />
                    <span className="flex-1">{t('workspaces.shareToWorkspace')}</span>
                    <ChevronRight className="size-4 text-fg-muted" />
                  </ContextMenu.SubTrigger>
                  <ContextMenu.Portal>
                    <ContextMenu.SubContent sideOffset={6} className={content}>
                      {writeWorkspaces.map((w) => (
                        <ContextMenu.Sub key={w.id}>
                          <ContextMenu.SubTrigger className={item}>
                            <span className="flex-1 truncate">{w.name}</span>
                            <ChevronRight className="size-4 text-fg-muted" />
                          </ContextMenu.SubTrigger>
                          <ContextMenu.Portal>
                            <ContextMenu.SubContent sideOffset={6} className={content}>
                              <ContextMenu.Item
                                className={item}
                                onSelect={() => void actions.shareToWorkspace(pageId, w.id)}
                              >
                                {t('workspaces.moveHere')}
                              </ContextMenu.Item>
                              <ContextMenu.Item
                                className={item}
                                onSelect={() => void actions.copyToWorkspace(pageId, w.id)}
                              >
                                {t('workspaces.copyHere')}
                              </ContextMenu.Item>
                            </ContextMenu.SubContent>
                          </ContextMenu.Portal>
                        </ContextMenu.Sub>
                      ))}
                    </ContextMenu.SubContent>
                  </ContextMenu.Portal>
                </ContextMenu.Sub>
              )}
            </>
          )}
          <ContextMenu.Separator className="my-1 h-px bg-border" />
          <ContextMenu.Item className={item} onSelect={() => actions.newTab(pageId)}>
            <ArrowUpRight className="size-4" />
            {t('pageMenu.newTab')}
            <Key keys={PAGE_KEYS.newTab} />
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
